/**
 * Collection Matcher Utilities
 * Functions for matching lyrics to collections based on keywords
 */

// Common words to ignore in matching (appear in too many songs)
const STOP_WORDS = new Set([
  'i', 'me', 'my', 'you', 'your', 'we', 'us', 'our', 'they', 'them', 'their',
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'can', "can't", 'cant', "won't", 'wont', "don't", 'dont', "didn't", 'didnt',
  'it', "it's", 'its', 'that', "that's", 'this', 'these', 'those',
  'and', 'or', 'but', 'if', 'then', 'so', 'just', 'now', 'here', 'there',
  'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'down',
  'out', 'off', 'over', 'under', 'again', 'all', 'any', 'both', 'each',
  'got', 'get', 'go', 'going', 'gone', 'come', 'came', 'coming',
  'know', 'see', 'look', 'like', 'want', 'need', 'take', 'make', 'let',
  'say', 'said', 'tell', 'told', 'think', 'feel', 'give', 'put',
  'oh', 'yeah', 'ya', 'na', 'la', 'da', 'ooh', 'ah', 'uh', 'hey',
  'when', 'where', 'what', 'who', 'how', 'why', 'which',
  'no', 'not', 'never', 'nothing', 'none', 'nobody',
  'some', 'something', 'someone', 'every', 'everything', 'everyone',
  'more', 'most', 'much', 'many', 'very', 'too', 'really', 'still',
  'only', 'even', 'also', 'well', 'way', 'back', 'time', 'day', 'night',
  'man', 'girl', 'boy', 'baby', 'one', 'two', 'first', 'last'
])

/**
 * Normalize text for matching
 * Lowercases, removes accents, removes special chars, normalizes whitespace
 */
export function normalize(text) {
  if (!text) return ''
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Check if an exact phrase appears in text
 */
export function exactPhraseMatch(phrase, text) {
  const normalizedText = normalize(text)
  const normalizedPhrase = normalize(phrase)
  return normalizedText.includes(normalizedPhrase)
}

/**
 * Check if a longer phrase has significant overlap (for 4+ word keywords)
 * Looks for consecutive word matches
 */
export function phraseOverlapMatch(phraseWords, text) {
  const normalizedText = normalize(text)

  // Check for any 3 consecutive words matching
  for (let i = 0; i < phraseWords.length - 2; i++) {
    const threeWords = phraseWords.slice(i, i + 3).join(' ')
    if (normalizedText.includes(threeWords)) {
      return true
    }
  }

  // Check for any 2 consecutive words matching (for shorter phrases)
  for (let i = 0; i < phraseWords.length - 1; i++) {
    const twoWords = phraseWords.slice(i, i + 2).join(' ')
    // Only count if neither word is a stop word
    if (normalizedText.includes(twoWords) &&
        !STOP_WORDS.has(phraseWords[i]) &&
        !STOP_WORDS.has(phraseWords[i + 1])) {
      return true
    }
  }

  return false
}

/**
 * Match lyrics against collection keywords
 * Returns top matches above threshold, sorted by score
 *
 * @param {string} lyrics - The lyrics text to match
 * @param {Array} collections - Array of collection objects with keywords
 * @param {number} threshold - Minimum score threshold (default 0.65)
 * @returns {Array} Top 5 matching collections with scores
 */
export function matchCollections(lyrics, collections, threshold = 0.65) {
  const matches = collections.map(collection => {
    let bestScore = 0
    let matchedKeyword = null

    for (const keyword of collection.keywords) {
      const keywordWords = normalize(keyword).split(' ').filter(Boolean)
      if (keywordWords.length === 0) continue

      // Filter out stop words from keyword
      const meaningfulKeywordWords = keywordWords.filter(w => !STOP_WORDS.has(w))

      // Skip if keyword is only stop words
      if (meaningfulKeywordWords.length === 0) continue

      // Skip single-word keywords (too broad, would match too many songs)
      if (meaningfulKeywordWords.length === 1) continue

      // Short phrases (2-3 words) - require EXACT phrase match
      if (keywordWords.length <= 3) {
        if (exactPhraseMatch(keyword, lyrics)) {
          bestScore = 1.0
          matchedKeyword = keyword
        }
        continue
      }

      // Longer phrases (4+ words) - allow partial consecutive match
      if (keywordWords.length >= 4) {
        if (exactPhraseMatch(keyword, lyrics)) {
          bestScore = 1.0
          matchedKeyword = keyword
        } else if (phraseOverlapMatch(keywordWords, lyrics) && 0.8 > bestScore) {
          bestScore = 0.8
          matchedKeyword = keyword
        }
      }
    }

    return {
      ...collection,
      score: bestScore,
      matchedKeyword
    }
  })

  return matches
    .filter(m => m.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)  // Top 5 for more options
}
